import type { AppState } from '@protector/entities/__legacy__/redux.types';
import { Entitlements } from '@protector/entities/licensing/constants';
import { NEMADIGITAL_APP_ID } from 'config/constants';
import { getSelectedPropertyId, getSystemFlags } from 'core/core.selectors';
import { useReaIntegrationMatch, useRegionalOverviewMatch, useReportsListMatch } from 'core/utils/hooks/usePathMatches';
import { hasRTRPlanEntitlement } from 'entities/company/company.selectors';
import { getPropertyEntities } from 'entities/property/property.selectors';
import { isEmpty } from 'lodash';
import { useNewHallOfCompanies } from 'pages/hall-of-entities/hall-of-entities-mfe.hooks';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

const usePropertyHasNoSeason = () => {
  const allProperties = useSelector(getPropertyEntities);
  const propertySelected = useSelector(getSelectedPropertyId);
  const isUsingNewHallOfCompanies = useNewHallOfCompanies();
  const showPropertiesWithoutSeasons = useSelector(hasRTRPlanEntitlement);
  const hideNemaCreateSeasonPopup = useSelector(
    (state: AppState) =>
      state.entities.company.licensingStatus?.apps.some(
        app =>
          app.id === NEMADIGITAL_APP_ID &&
          app.plan.entitlements.some(entitlement => entitlement.key === Entitlements.HIDE_NEMA_CREATE_SEASON_POPUP)
      ) ?? false
  );

  const reaIntegrationMatch = useReaIntegrationMatch();
  const regionalOverviewMatch = useRegionalOverviewMatch();
  const reportsListMatch = useReportsListMatch();

  const systemFlags = useSelector(getSystemFlags);
  const enableModalNoSeasons = systemFlags?.P40_52358_enable_modal_no_seasons as boolean;

  const propertyHasNoSeason = useMemo(() => {
    if (!enableModalNoSeasons) return false;
    if (reaIntegrationMatch) return false;
    if (regionalOverviewMatch) return false;
    if (reportsListMatch) return false;
    if (hideNemaCreateSeasonPopup) return false;

    if (isEmpty(allProperties) || !isUsingNewHallOfCompanies || !propertySelected || showPropertiesWithoutSeasons) return false;
    const proprieties = Object.values(allProperties);
    const property = proprieties.find(property => property.id === propertySelected);

    if (!property) return false;

    return !property.seasons.length;
  }, [
    allProperties,
    enableModalNoSeasons,
    hideNemaCreateSeasonPopup,
    isUsingNewHallOfCompanies,
    propertySelected,
    regionalOverviewMatch,
    reaIntegrationMatch,
    reportsListMatch,
    showPropertiesWithoutSeasons
  ]);

  return propertyHasNoSeason;
};

export { usePropertyHasNoSeason };
